import React, { useEffect, useState, useCallback } from "react";
import { api } from "../config/api";
import { useLocation } from "../contexts/LocationContext";
import "./NearbyLocationsList.css";

const PRIMARY_RADIUS_KM = 25;
const MAX_RESULTS = 1;
const DEBUG = false;

const log = (...args) => {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log("[NearbyLocationsList]", ...args);
  }
};

const calculateHaversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Rough road-distance estimate from straight-line: multiply by ~1.3 (avg
// road-network ratio for Indian cities). Only used when road API isn't called.
const estimateRoadKm = (haversineKm) =>
  Number.isFinite(haversineKm) ? haversineKm * 1.3 : null;

// Best-effort road distance via OSRM public demo. No API key needed. Returns
// metres or null on failure. We always fall back to the haversine estimate so
// the UI is never empty.
const fetchRoadDistanceMetres = async (originLat, originLng, destLat, destLng) => {
  try {
    const url =
      `https://router.project-osrm.org/route/v1/driving/` +
      `${originLng},${originLat};${destLng},${destLat}` +
      `?overview=false`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const route = data?.routes?.[0];
    if (!route || typeof route.distance !== "number") return null;
    return route.distance / 1000; // metres -> km
  } catch {
    return null;
  }
};

const formatDistance = (km) => {
  if (!Number.isFinite(km)) return "—";
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)} km`;
};

// Sort cinemas so the *closest to current location* (by straight-line) is
// first, then group by city and keep only the closest cinema per city so
// the "1 cinema per city" count is real, not a sum of duplicates.
const pickNearestCities = (cinemas, loc, selectedCityName, limit) => {
  const ranked = [...cinemas]
    .map((c) => {
      let distance = null;
      if (Number.isFinite(c.distance)) {
        distance = Number(c.distance);
      } else {
        const lat = c.latitude ?? c.lat;
        const lng = c.longitude ?? c.lng;
        if (
          loc &&
          Number.isFinite(lat) &&
          Number.isFinite(lng)
        ) {
          distance = calculateHaversineKm(loc.lat, loc.lng, lat, lng);
        }
      }
      return { ...c, _distance: distance };
    })
    .filter((c) => Number.isFinite(c._distance))
    .filter(
      (c) =>
        !selectedCityName ||
        String(c.city || "").toLowerCase() !== selectedCityName.toLowerCase()
    )
    .sort((a, b) => a._distance - b._distance);

  log("Cinemas sorted by distance", {
    total: ranked.length,
    top5: ranked.slice(0, 5).map((c) => ({
      name: c.name,
      city: c.city,
      km: c._distance.toFixed(1),
    })),
  });

  const grouped = new Map();
  for (const cinema of ranked) {
    const cityName = String(cinema.city || "").trim();
    if (!cityName) continue;
    if (!grouped.has(cityName)) {
      grouped.set(cityName, {
        cityName,
        count: 0,
        distance: cinema._distance,
        cinema: cinema,
      });
    }
    grouped.get(cityName).count += 1;
  }

  const uniqueByDistance = Array.from(grouped.values()).sort(
    (a, b) => a.distance - b.distance
  );

  log("Cities ranked by nearest cinema", {
    total: uniqueByDistance.length,
    picked: uniqueByDistance.slice(0, limit).map((g) => ({
      city: g.cityName,
      cinemas: g.count,
      km: g.distance.toFixed(1),
    })),
  });

  return uniqueByDistance.slice(0, limit);
};

const NearbyLocationsList = () => {
  const { getLocationForAPI, allCities, selectedCity, selectCity } = useLocation();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [roadDistances, setRoadDistances] = useState({});
  const [debugInfo, setDebugInfo] = useState({
    primaryCount: 0,
    fallbackCount: 0,
    finalCount: 0,
    pathTaken: "none",
    lat: null,
    lng: null,
  });

  const fetchNearby = useCallback(async () => {
    const loc = getLocationForAPI();
    // Prefer the selected city's coordinates for distance ranking. Once a
    // city has been picked (via LocationDropdown or our own click handler),
    // its lat/lng is the most accurate "centre" for that location. Falling
    // back to GPS avoids stale-coords bugs where the user picked Angamaly
    // but the device is still locked on Anekal GPS (12.81, 77.70).
    const rankingLoc =
      selectedCity &&
      Number.isFinite(selectedCity.lat) &&
      Number.isFinite(selectedCity.lng)
        ? {
            lat: selectedCity.lat,
            lng: selectedCity.lng,
            source: "selected-city",
            cityName: selectedCity.name,
          }
        : loc;

    log("Mount", {
      selectedCity: selectedCity?.name,
      selectedCityLat: selectedCity?.lat,
      selectedCityLng: selectedCity?.lng,
      gpsLat: loc?.lat,
      gpsLng: loc?.lng,
      gpsSource: loc?.source,
      rankingFrom: rankingLoc?.source,
    });

    if (!rankingLoc || rankingLoc.lat == null || rankingLoc.lng == null) {
      log("No GPS or selected-city coords — using allCities fallback");
      const all = Array.isArray(allCities) ? allCities : [];
      const fallback = all
        .filter((c) => c && c.name)
        .filter(
          (c) =>
            !selectedCity?.name ||
            c.name.toLowerCase() !== selectedCity.name.toLowerCase()
        )
        .slice(0, MAX_RESULTS)
        .map((c) => ({
          cityName: c.name,
          count: 0,
          distance: null,
          cinema: null,
        }));
      setGroups(fallback);
      setDebugInfo({
        primaryCount: 0,
        fallbackCount: 0,
        finalCount: fallback.length,
        pathTaken: "no-location-cities-fallback",
        lat: null,
        lng: null,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    let primaryCount = 0;
    let fallbackCount = 0;
    let pathTaken = "primary-25km";
    let finalGroups = [];

    try {
      // ── Step 1: try the standard 25 km radius first ──
      log("Step 1: 25 km radius fetch", {
        from: rankingLoc.source,
        lat: rankingLoc.lat,
        lng: rankingLoc.lng,
      });
      const near = await api.getCinemas({
        latitude: rankingLoc.lat,
        longitude: rankingLoc.lng,
        maxDistance: PRIMARY_RADIUS_KM,
        limit: 100,
      });
      const nearList = Array.isArray(near) ? near : [];
      primaryCount = nearList.length;
      log("Step 1 result", { count: primaryCount });

      let cinemasForRanking = nearList;

      if (primaryCount === 0) {
        // ── Step 2: empty within 25 km → pull EVERY cinema (unlimited) ──
        log("Step 2: 25 km empty, fetching all cinemas globally (unlimited)");
        const all = await api.getCinemas({
          latitude: rankingLoc.lat,
          longitude: rankingLoc.lng,
          unlimited: true,
          limit: 1000,
        });
        const allList = Array.isArray(all) ? all : [];
        fallbackCount = allList.length;
        pathTaken = "unlimited-nearest";
        log("Step 2 result", {
          count: fallbackCount,
          first3: allList.slice(0, 3).map((c) => ({
            name: c.name,
            city: c.city,
            km: c.distance,
          })),
        });
        cinemasForRanking = allList;
      }

      finalGroups = pickNearestCities(
        cinemasForRanking,
        rankingLoc,
        selectedCity?.name,
        MAX_RESULTS
      );

      log("Final pick", {
        count: finalGroups.length,
        groups: finalGroups.map((g) => ({
          city: g.cityName,
          cinemas: g.count,
          haversine_km: g.distance.toFixed(1),
        })),
      });

      // Try to fetch actual road distance for the picked nearest location so
      // the user sees "325 km (road)" instead of straight-line.
      if (finalGroups.length && finalGroups[0].cinema) {
        const top = finalGroups[0];
        const cinemaLat = top.cinema.latitude ?? top.cinema.lat;
        const cinemaLng = top.cinema.longitude ?? top.cinema.lng;
        if (Number.isFinite(cinemaLat) && Number.isFinite(cinemaLng)) {
          log("Fetching road distance from rankingLoc to nearest cinema");
          const roadKm = await fetchRoadDistanceMetres(
            rankingLoc.lat,
            rankingLoc.lng,
            cinemaLat,
            cinemaLng
          );
          if (Number.isFinite(roadKm)) {
            log("Road distance", {
              city: top.cityName,
              road_km: roadKm.toFixed(1),
              haversine_km: top.distance.toFixed(1),
              diff_pct: (((roadKm - top.distance) / top.distance) * 100).toFixed(0),
            });
            setRoadDistances({ [top.cityName]: roadKm });
          } else {
            log("Road lookup failed, using haversine estimate", {
              haversine_km: top.distance.toFixed(1),
              road_estimate_km: estimateRoadKm(top.distance)?.toFixed(1),
            });
          }
        }
      }

      setGroups(finalGroups);
      setDebugInfo({
        primaryCount,
        fallbackCount,
        finalCount: finalGroups.length,
        pathTaken,
        lat: rankingLoc.lat,
        lng: rankingLoc.lng,
      });
    } catch (err) {
      log("Fetch failed", { error: err?.message });
      setGroups([]);
      setDebugInfo({
        primaryCount,
        fallbackCount,
        finalCount: 0,
        pathTaken: "error",
        lat: rankingLoc.lat,
        lng: rankingLoc.lng,
      });
    } finally {
      setLoading(false);
    }
  }, [getLocationForAPI, selectedCity, allCities]);

  useEffect(() => {
    fetchNearby();
  }, [fetchNearby]);

  const handleSelect = (cityName) => {
    const trimmed = String(cityName || "").trim();
    if (!trimmed) {
      log("Click: empty city name, skipping");
      return;
    }
    const match = (allCities || []).find(
      (c) => c && c.name && c.name.toLowerCase() === trimmed.toLowerCase()
    );
    log("Click", { cityName, matched: !!match, matchId: match?.id });
    if (match) {
      selectCity(match);
    }
  };

  if (loading) {
    return (
      <div className="nl-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Looking for nearby locations…</span>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="nl-empty">
        <p>No MY Cinemas locations found yet.</p>
        {DEBUG && (
          <div className="nl-debug">
            <small>
              🪲 no groups — lat: {String(debugInfo.lat)} · lng: {String(
                debugInfo.lng
              )}{" "}
              · 25km: {debugInfo.primaryCount} · all: {debugInfo.fallbackCount} ·
              path: {debugInfo.pathTaken}
            </small>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="nl-wrapper">
      <h5 className="nl-title">Nearest MY Cinemas location</h5>
      {DEBUG && (
        <div className="nl-debug">
          <small>
            🪲 lat:{" "}
            {Number.isFinite(debugInfo.lat)
              ? debugInfo.lat.toFixed(3)
              : "—"}{" "}
            · lng:{" "}
            {Number.isFinite(debugInfo.lng) ? debugInfo.lng.toFixed(3) : "—"}{" "}
            · 25km: {debugInfo.primaryCount} · all:{" "}
            {debugInfo.fallbackCount} · path:{" "}
            <strong>{debugInfo.pathTaken}</strong>
            {debugInfo.pathTaken === "unlimited-nearest" && (
              <span>
                {" "}
                · (none in 25 km, picked nearest city globally)
              </span>
            )}
          </small>
        </div>
      )}
      <div className="nl-grid nl-grid-single">
        {groups.map((g) => {
          const roadKm = roadDistances[g.cityName];
          const haversineKm = g.distance;
          const displayKm =
            Number.isFinite(roadKm)
              ? roadKm
              : estimateRoadKm(haversineKm) ?? haversineKm;
          return (
            <button
              key={g.cityName}
              type="button"
              className="nl-card nl-card-single"
              onClick={() => handleSelect(g.cityName)}
            >
              <div className="nl-card-name">{g.cityName}</div>
              <div className="nl-card-meta">
                <span className="nl-dist">
                  <i className="fas fa-location-dot"></i>{" "}
                  {formatDistance(displayKm)} away
                </span>
                <span className="nl-count">
                  <i className="fas fa-film"></i> {g.count} cinema
                  {g.count !== 1 ? "s" : ""} available
                </span>
              </div>
              <div className="nl-card-cta">
                View cinemas <i className="fas fa-arrow-right"></i>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NearbyLocationsList;
